/**
 * VariationAnalysis is the main component of the page used to add, edit and delete variation analyses.
 * It is encapsulated in a PyramidCard.
 * This component contains the context and the subcomponents used in this page: Datagrid and AddEditPopup.
 * This component retrieves data from the API and transmits it to the subcomponents.
 */

import React, { useState, useEffect } from "react";
import Datagrid from "./Datagrid";
import AddEditPopup from "./AddEditPopup";
import { horusApi } from "../../../../../services/api/horus";
import { useAuth } from "../../../../../providers/AuthProvider";
import PyramidCard from "../../../../../components/common/pyramid/PyramidCard";
import notify from "devextreme/ui/notify";

const VariationAnalysisAdd = () => {
  const api = horusApi();
  const { accessToken, companyList, userList } = useAuth();

  /* State variables */
  const [isLoading, setIsLoading] = useState(true);
  const [variationAnalyses, setVariationAnalyses] = useState([]);
  const [datasets, setDatasets] = useState([]);
  const [editingVariationAnalysis, setEditingVariationAnalysis] =
    useState(null);

  const fetchData = async () => {
    if (!accessToken) {
      return;
    }

    setIsLoading(true);
    try {
      const [datasetsData, variationAnalysesData] = await Promise.all([
        api.getDatasets({
          onlyReady: true,
          includeUserTransitiveGroups: true,
        }),
        api.getVariations({
          onlyReady: false,
          includeUserTransitiveGroups: true,
        }),
      ]);
      const variationAnalyses = Array.isArray(variationAnalysesData)
        ? variationAnalysesData
        : variationAnalysesData.result || [];
      const datasets = Array.isArray(datasetsData)
        ? datasetsData
        : datasetsData.result || [];
      setVariationAnalyses(variationAnalyses);
      setDatasets(datasets);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [accessToken]);

  useEffect(() => {
    const handleRefresh = () => {
      fetchData();
    };

    window.addEventListener("refreshVariationAnalyses", handleRefresh);
    return () =>
      window.removeEventListener("refreshVariationAnalyses", handleRefresh);
  }, [accessToken]);

  // Uses the editingVariationAnalysis state to determine if the add/edit popup is visible and if we are in edit mode
  const isAddEditPopupVisible = Boolean(editingVariationAnalysis !== null);
  const editMode = Boolean(editingVariationAnalysis?.id);

  const handleRowClick = (rowData) => {
    setEditingVariationAnalysis(rowData);
  };

  /**
   * Opens the add popup in add mode.
   */
  const handleAddButtonClick = () => {
    setEditingVariationAnalysis({});
  };

  /**
   * Handles the click on the cancel button and closes the add/edit popup by setting the editingVariationAnalysis state to null.
   */
  const onCancel = () => {
    setEditingVariationAnalysis(null);
  };

  const onConfirm = async (data) => {
    try {
      if (editMode) {
        const updatedData = {
          id: editingVariationAnalysis.id,
          description: data.description,
        };
        await api.updateVariationAnalysis(updatedData);
        fetchData();
        setEditingVariationAnalysis(null);
      } else {
        const newData = {
          description: data.description,
          couponMethod: data.couponMethod,
          datasetDepartureId: data.datasetDepartureId,
          datasetArrivalId: data.datasetArrivalId,
          actuarialRateRulesDeparture: data.actuarialRateRulesDeparture ?? 0,
          actuarialRateRulesArrival: data.actuarialRateRulesArrival ?? 0,
        };

        await api.addVariationAnalysis(newData);
        fetchData();
        setEditingVariationAnalysis(null);
      }
    } catch (error) {
      console.error("Error confirming data:", error.message);
      notify(error.message || "An error occurred", "error", 3000);
    }
  };

  return (
    <div>
      <Datagrid
        companyList={companyList}
        userList={userList}
        variationList={variationAnalyses || []}
        datasetList={datasets || []}
        onAddButtonClick={handleAddButtonClick}
        onRowClick={handleRowClick}
        isLoading={isLoading}
      />
      <AddEditPopup
        isVisible={isAddEditPopupVisible}
        onCancel={onCancel}
        onConfirm={onConfirm}
        inputFormData={editingVariationAnalysis || {}}
        datasetList={datasets || []}
        editMode={editMode}
      />
    </div>
  );
};

/**
 * Page component using PyramidCard
 */
const VariationAnalysisAddHorus = () => {
  return (
    <PyramidCard title="Add Variation Analysis">
      <VariationAnalysisAdd />
    </PyramidCard>
  );
};

export default VariationAnalysisAddHorus;
